import {
    Component,
    ViewChild,
    OnInit,
    Input,
    Output,
    EventEmitter,
    OnChanges,
    SimpleChanges,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { Router, RouterModule } from "@angular/router";
import { OverlayPanelModule } from "primeng/overlaypanel";
import { OverlayPanel } from "primeng/overlaypanel";
import { SidebarModule } from "primeng/sidebar";
import { Sidebar } from "primeng/sidebar";
import { SharedComponentsModule } from "@/components/shared/shared-components.module";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { ResponsiveService } from "@/components/shared/responsive/responsive.service";
import { UserService } from "@/_services";
import { AccountService } from "@/_services/account.service";
import { OnboardingGuideService } from "@/_services/onboarding-guide.service";
import { TerminologyService } from "@/_services/terminology/terminology.service";
import { TermKey } from "@/_services/terminology/catalog";
import {
    ASAP_QR_STEP,
    ASAP_RECIPIENTS_STEP,
    isAsapStepList,
    OnboardingStepResponse,
    SetupSection,
    STEPS_NEEDING_ASAP_FORM,
} from "@/_models/onboarding-step-response.interface";
import { ToastService } from "@/_services/ToastService";

const SECTION_ORDER: SetupSection[] = ["account", "content", "team"];

const STEP_BODY_EXPAND_TRANSITION_MS = 300;
const TRANSITION_SETTLE_BUFFER_MS = 20;
const MOBILE_SIDEBAR_CLOSE_MS = 100;

interface DisplayStep {
    key: string;
    section: SetupSection;
    title: string;
    body: string;
    cta: string;
    route: string;
    completed: boolean;
    notApplicable: boolean;
    dismissible: boolean;
    enabled: boolean;
    isOpen: boolean;
}

interface DisplaySection {
    key: SetupSection;
    title: string;
    steps: DisplayStep[];
    completedCount: number;
    complete: boolean;
    isOpen: boolean;
}

@UntilDestroy()
@Component({
    selector: "app-onboarding-guide",
    standalone: true,
    imports: [
        CommonModule,
        RouterModule,
        OverlayPanelModule,
        SidebarModule,
        SharedComponentsModule,
    ],
    styleUrls: ["./onboarding-guide.component.scss"],
    templateUrl: "./onboarding-guide.component.html",
})
export class OnboardingGuideComponent implements OnInit, OnChanges {
    @ViewChild("op") op!: OverlayPanel;
    @ViewChild("sidebar") sidebar!: Sidebar;
    @Input() asapFormId: string | null = null;
    @Input() steps: OnboardingStepResponse[] = [];
    @Output() stepsChange = new EventEmitter<OnboardingStepResponse[]>();

    displaySections: DisplaySection[] = [];
    displaySteps: DisplayStep[] = [];
    showSectionHeaders = false;
    guideTitle = "";
    notApplicableLabel = "";
    restoreLabel = "";
    isMobile = false;
    sidebarVisible = false;
    completedSteps = 0;
    maxSteps = 0;

    constructor(
        private router: Router,
        private responsiveService: ResponsiveService,
        private userService: UserService,
        private accountService: AccountService,
        private onboardingGuideService: OnboardingGuideService,
        private terminology: TerminologyService,
        private toastService: ToastService,
    ) {}

    ngOnInit(): void {
        this.checkInitialScreenSize();
        this.followResponsiveBreakpoint();
        this.rebuildCopyOnceUserIsKnown();
    }

    private followResponsiveBreakpoint(): void {
        this.responsiveService.responsiveChangeSubject
            .pipe(untilDestroyed(this))
            .subscribe(() => {
                this.isMobile = this.responsiveService.isMobile;
            });
    }

    private rebuildCopyOnceUserIsKnown(): void {
        this.userService
            .getCurrentUserAsync()
            .pipe(untilDestroyed(this))
            .subscribe(() => this.updateDisplaySteps());
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes["steps"]) {
            this.updateDisplaySteps();
        }
    }

    private get isAsapAccount(): boolean {
        return isAsapStepList(this.steps);
    }

    private term(key: string): string {
        return this.terminology.resolve(key as TermKey);
    }

    private updateDisplaySteps(): void {
        this.guideTitle = this.term(
            this.isAsapAccount ? "setup.title.asap" : "setup.title",
        );
        this.notApplicableLabel = this.term("setup.action.notApplicable");
        this.restoreLabel = this.term("setup.action.markApplicable");

        if (!this.steps || this.steps.length === 0) {
            this.displaySections = [];
            this.displaySteps = [];
            this.completedSteps = 0;
            this.maxSteps = 0;
            return;
        }

        const previousSteps = this.displaySteps;
        const previousSections = this.displaySections;

        const built = this.steps.map((step) => this.toDisplay(step));
        built.forEach((step) => {
            const previous = previousSteps.find(
                (candidate) => candidate.key === step.key,
            );
            if (previous) {
                step.isOpen = previous.isOpen;
            }
        });

        this.displaySteps = built;
        this.completedSteps = built.filter((step) => step.completed).length;
        this.maxSteps = built.length;
        this.displaySections = SECTION_ORDER.map((section) => {
            const steps = built.filter((step) => step.section === section);
            const completedCount = steps.filter(
                (step) => step.completed,
            ).length;
            const previous = previousSections.find(
                (candidate) => candidate.key === section,
            );
            return {
                key: section,
                title: this.term(`setup.section.${section}`),
                steps,
                completedCount,
                complete: steps.length > 0 && completedCount === steps.length,
                isOpen: previous?.isOpen ?? false,
            };
        }).filter((section) => section.steps.length > 0);
        this.showSectionHeaders = this.displaySections.length > 1;

        if (!this.displaySections.some((section) => section.isOpen)) {
            this.openFirstOutstandingSection();
        }
    }

    private openFirstOutstandingSection(): void {
        const target =
            this.displaySections.find((section) => !section.complete) ??
            this.displaySections[0];
        if (!target) {
            return;
        }
        target.isOpen = true;

        const step = this.firstOutstandingOrFirst(target.steps);
        if (step) {
            step.isOpen = true;
        }
    }

    private firstOutstandingOrFirst(
        steps: DisplayStep[],
    ): DisplayStep | undefined {
        return steps.find((candidate) => !candidate.completed) ?? steps[0];
    }

    private toDisplay(step: OnboardingStepResponse): DisplayStep {
        return {
            key: step.step,
            section: step.section,
            title: this.term(`setup.step.${step.step}.title`),
            body: this.term(`setup.step.${step.step}.body`),
            cta: this.term(`setup.step.${step.step}.cta`),
            route: step.route,
            completed: step.completed,
            notApplicable: step.notApplicable,
            dismissible: step.dismissible,
            enabled: this.isStepEnabled(step),
            isOpen: false,
        };
    }

    private isStepEnabled(step: OnboardingStepResponse): boolean {
        if (STEPS_NEEDING_ASAP_FORM.includes(step.step)) {
            return !!this.asapFormId;
        }
        return !!step.route;
    }

    private checkInitialScreenSize() {
        if (this.responsiveService.isMobile !== undefined) {
            this.isMobile = this.responsiveService.isMobile;
        }
    }

    toggleSectionGroup(sectionKey: SetupSection) {
        const section = this.displaySections.find(
            (candidate) => candidate.key === sectionKey,
        );
        if (!section) {
            return;
        }

        const opening = !section.isOpen;
        this.displaySections.forEach(
            (candidate) =>
                (candidate.isOpen = opening && candidate === section),
        );

        if (opening && section.steps.every((step) => !step.isOpen)) {
            const step = this.firstOutstandingOrFirst(section.steps);
            if (step) {
                this.displaySteps.forEach(
                    (candidate) => (candidate.isOpen = candidate === step),
                );
            }
        }

        this.realignOverlayToTarget();
    }

    toggleStep(stepKey: string) {
        const step = this.displaySteps.find((s) => s.key === stepKey);
        if (!step) return;

        const opening = !step.isOpen;
        this.displaySteps.forEach(
            (candidate) => (candidate.isOpen = opening && candidate === step),
        );

        this.realignOverlayToTarget();
    }

    private realignOverlayToTarget(): void {
        if (this.isMobile || !this.op) {
            return;
        }
        setTimeout(() => this.realignOverlay());
        setTimeout(
            () => this.realignOverlay(),
            STEP_BODY_EXPAND_TRANSITION_MS + TRANSITION_SETTLE_BUFFER_MS,
        );
    }

    private realignOverlay(): void {
        if (!this.op?.container) {
            return;
        }
        this.op.container.classList.remove("p-overlaypanel-flipped");
        this.op.align();
    }

    toggle(event: Event) {
        if (this.isMobile) {
            this.sidebarVisible = !this.sidebarVisible;
        } else {
            if (this.op) {
                this.op.toggle(event, event.target);
            }
        }
    }

    hide() {
        if (this.isMobile) {
            this.sidebarVisible = false;
        } else {
            if (this.op) this.op.hide();
        }
        this.onboardingGuideService.close();
    }

    executeStepAction(stepKey: string) {
        const step = this.displaySteps.find((s) => s.key === stepKey);
        if (!step || !step.enabled) {
            return;
        }

        if (stepKey === ASAP_QR_STEP) {
            this.printAsapQrCode();
            return;
        }

        const destination =
            stepKey === ASAP_RECIPIENTS_STEP
                ? `/assessments/${this.asapFormId}/edit/recipients`
                : step.route;

        if (this.isMobile) {
            this.hide();
            setTimeout(
                () => this.router.navigateByUrl(destination),
                MOBILE_SIDEBAR_CLOSE_MS,
            );
            return;
        }
        this.router.navigateByUrl(destination);
    }

    private printAsapQrCode() {
        if (!this.asapFormId) {
            return;
        }
        window.open(
            `/api/assessments/${this.asapFormId}/qr-code?format=pdf`,
            "_blank",
        );
        this.onboardingGuideService
            .refresh(this.asapFormId)
            .pipe(untilDestroyed(this))
            .subscribe();
        if (this.isMobile) {
            this.hide();
        }
    }

    markAsNotApplicable(stepKey: string) {
        const step = this.displaySteps.find((s) => s.key === stepKey);
        if (!step || step.completed || !step.dismissible) {
            return;
        }
        this.setNotApplicable(stepKey, true);
    }

    restoreStep(stepKey: string) {
        const step = this.displaySteps.find((s) => s.key === stepKey);
        if (!step || !step.notApplicable) {
            return;
        }
        this.setNotApplicable(stepKey, false);
    }

    private setNotApplicable(stepKey: string, notApplicable: boolean) {
        this.accountService
            .setSetupStepNotApplicable(stepKey, notApplicable)
            .pipe(untilDestroyed(this))
            .subscribe({
                next: () => {
                    this.onboardingGuideService
                        .refresh(this.asapFormId || undefined)
                        .pipe(untilDestroyed(this))
                        .subscribe((steps) => this.stepsChange.emit(steps));
                },
                error: () => {
                    this.toastService.error(
                        notApplicable
                            ? "Failed to mark step as not applicable"
                            : "Failed to restore step",
                    );
                },
            });
    }
}
